<template>
  <v-col class="video-page-settings pa-0">
    <v-row no-gutters>
      <v-col cols="12" class="px-3 pb-2">
        <span class="body-bold-medium">{{
          $t('message.pages.videoPage.settings.title')
        }}</span>
      </v-col>
      <v-row
        no-gutters
        class="pa-0 mx-3"
        :data-step="2"
        :data-title="$t(introPath + 'search.title')"
        :data-intro="$t(introPath + 'search.description')"
      >
        <v-col cols="12">
          <SearchBar
            v-show="!isMobileBreakpoint"
            :list="searchList"
            :fuseOptions="searchFuseOptions"
            :isPandaAiEnabledAndReady="isPandaAiEnabledAndReady"
            :placeholder="
              $t('message.pages.videoPage.settings.searchPlaceholder')
            "
            @openAnalyticsNewTab="handleOpenAnalytics"
          />
        </v-col>
      </v-row>
    </v-row>
    <span
      :data-step="3"
      :data-title="$t(introPath + 'settings.title')"
      :data-intro="$t(introPath + 'settings.description')"
    >
      <v-row no-gutters>
        <v-col class="pa-0">
          <h3 v-if="readyForCustomize" class="body-label-caption px-3 mt-10">
            {{ $t('message.pages.video.playerOptionTitles.basic') }}
          </h3>
          <v-card flat tile class="d-flex flex-column text-left">
            <!-- colors -->
            <v-btn
              id="colors"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('colors')"
              text
            >
              <span class="d-flex">
                <span class="settings-btn--text">
                  {{ $t('message.pages.video.colors') }}
                </span>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- controls -->
            <v-btn
              id="controls"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('controls')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoPage.settings.options.controls') }}
                </p>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- Big play button -->
            <v-btn
              id="big-play-button"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('bigPlayButton')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{
                    $t('message.pages.videoPage.settings.options.bigPlayButton')
                  }}
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.bigPlayButton"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- subtitles -->
            <v-tooltip
              v-if="!video.hasOwnProperty('video_source')"
              v-model="showConversionTooltip.sub"
              bottom
              content-class="converting-video-tooltip--offset"
            >
              <template v-slot:activator="{ props }">
                <div
                  class="w-fill"
                  :class="{ 'cursor-disabled': isConverting }"
                  @mouseenter="handleConversionTooltip($event, 'sub')"
                  @mouseleave="handleConversionTooltip($event, 'sub')"
                  v-bind="props"
                >
                  <v-btn
                    id="subtitle"
                    class="settings-btn py-0 px-3"
                    x-large
                    @click="handleSelectOption('subtitles')"
                    style="width: 100%"
                    text
                    :disabled="isConverting || !isPlayable"
                  >
                    <span class="d-flex">
                      <p
                        class="settings-btn--text"
                        :class="{ 'color-disabled': isConverting }"
                      >
                        {{ $t('message.pages.videoEditModal.subtitles') }}
                      </p>
                    </span>
                    <div
                      class="d-flex justify-end align-center"
                      style="width: 100%"
                    >
                      <FlagTooltip
                        :items="subtitlesTooltipInfo"
                        :originalLang="video.original_lang || ''"
                        v-if="subtitlesTooltipInfo"
                      >
                        <FlagCards
                          :items="
                            getItemsCountryCode(
                              subtitlesTooltipInfo.subtitles.items
                            )
                          "
                        />
                      </FlagTooltip>
                      <span v-if="checkForSubtitles" class="ml-3">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <div v-on="on" class="d-inline-block"></div>
                          </template>
                          <span
                            >{{ video.config.subtitles.length }}
                            {{
                              video.config.subtitles.length > 1
                                ? $t('message.pages.video.subtitles')
                                : $t('message.pages.video.subtitle')
                            }}</span
                          >
                        </v-tooltip>
                      </span>
                    </div>
                    <v-icon
                      class="settings-btn--icon"
                      :class="{ 'color-disabled': isConverting }"
                      size="16"
                      >panda-go</v-icon
                    >
                  </v-btn>
                </div>
              </template>
              <span
                class="body-bold-smaller"
                v-html="
                  $t('message.pages.videoPage.details.alert.converting.tooltip')
                "
              ></span>
            </v-tooltip>

            <v-tooltip
              v-if="!video.hasOwnProperty('video_source')"
              v-model="showConversionTooltip.dub"
              content-class="converting-video-tooltip--offset"
              bottom
            >
              <template v-slot:activator="{ props }">
                <div
                  class="w-fill"
                  :class="{ 'cursor-disabled': isConverting }"
                  @mouseenter="handleConversionTooltip($event, 'dub')"
                  @mouseleave="handleConversionTooltip($event, 'dub')"
                  v-bind="props"
                >
                  <v-btn
                    id="dubbing"
                    class="settings-btn py-0 px-3"
                    x-large
                    @click="handleSelectOption('dubbing')"
                    style="width: 100%"
                    text
                    :disabled="isConverting || !isPlayable"
                  >
                    <span class="d-flex">
                      <p
                        class="settings-btn--text"
                        :class="{ 'color-disabled': isConverting }"
                      >
                        {{ $i18next.t('message.ai.dubbings.dubbing') }}
                      </p>
                    </span>
                    <div
                      v-if="activeAudios && activeAudios.length"
                      class="d-flex justify-end align-center"
                      style="width: 100%"
                    >
                      <FlagTooltip
                        :items="audiosTooltipInfo"
                        :originalLang="video.original_lang || ''"
                        v-if="audiosTooltipInfo"
                      >
                        <FlagCards
                          :items="
                            getItemsCountryCode(audiosTooltipInfo.audios.items)
                          "
                        />
                      </FlagTooltip>
                      <span v-if="activeAudios" class="ml-3">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <div v-on="on" class="d-inline-block"></div>
                          </template>
                          <span
                            >{{ activeAudios.length }}
                            {{
                              activeAudios.length > 1
                                ? $i18next.t('message.ai.dubbings.dubbings')
                                : $i18next.t('message.ai.dubbings.dubbing')
                            }}</span
                          >
                        </v-tooltip>
                      </span>
                    </div>
                    <v-icon
                      class="settings-btn--icon"
                      :class="{ 'color-disabled': isConverting }"
                      size="16"
                      >panda-go</v-icon
                    >
                  </v-btn>
                </div>
              </template>
              <span
                class="body-bold-smaller"
                v-html="
                  $t('message.pages.videoPage.details.alert.converting.tooltip')
                "
              ></span>
            </v-tooltip>

            <!-- thumbnails -->
            <v-btn
              id="thumbnails"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('thumbnails')"
              :disabled="!canShowTimeOptions"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{
                    $t('message.pages.videoPage.settings.options.thumbnails')
                  }}
                </p>
              </span>
              <!-- <div>
                <v-progress-circular
                  v-if="!canShowTimeOptions"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div> -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.thumbnails"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- chapters -->
            <v-btn
              id="chapters"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('chapters')"
              :disabled="!canShowTimeOptions"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.video.chapters') }}
                </p>
              </span>
              <!-- <div
                v-if="video.config.bookMarks"
                class="d-flex"
                style="width: 100%; justify-content: right"
              >
                <span
                  v-if="
                    !video.config.bookMarks || video.config.bookMarks.length !== 0
                  "
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="d-inline-block">
                        <span class="circle-player"></span>
                      </div>
                    </template>
                    <span
                      >{{ video.config.bookMarks.length }}
                      {{
                        video.config.bookMarks.length > 1
                          ? $t('message.pages.video.chapters')
                          : $t('message.pages.video.chapter')
                      }}</span
                    >
                  </v-tooltip>
                </span>
              </div>
              <div>
                <v-progress-circular
                  v-if="!canShowTimeOptions"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div> -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.bookMarks"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    id="subtitles"
                    class="settings-btn py-0 px-3"
                    x-large
                    text
                    :disabled="true"
                    style="width: 100%"
                  >
                    <span class="d-flex">
                      <v-icon>mdi-comment-text-outline</v-icon>
                      <p class="settings-btn--text">
                        {{ $t('message.pages.videoEditModal.subtitles') }}
                      </p>
                    </span>
                    <v-icon class="settings-btn--icon" size="16"
                      >panda-go</v-icon
                    >
                  </v-btn>
                </div>
              </template>
              <span>{{
                $t('message.pages.externalSourceVideo.unavailableFor')
              }}</span>
            </v-tooltip>
          </v-card>
        </v-col>
      </v-row>
      <options-ai
        ref="PandaAiSettings"
        :canSeePlanInfo="canSeePlanInfo"
        :isBeta="isBeta"
        :handleSelectOption="handleSelectOption"
        :isConverting="isConverting"
        @isEnabledAndReady="(val) => (isPandaAiEnabledAndReady = val)"
      ></options-ai>
      <v-row no-gutters class="mt-2">
        <v-col class="pa-0">
          <h3 v-if="readyForCustomize" class="body-label-caption px-3 mt-10">
            {{ $t('message.pages.video.playerOptionTitles.advanced') }}
          </h3>
          <v-card flat tile class="d-flex flex-column text-left">
            <!-- mini hook -->
            <v-btn
              id="mini-trigger"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('minitrigger')"
              :disabled="!canShowTimeOptions"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{
                    $t('message.pages.videoPage.settings.options.miniTriggers')
                  }}
                </p>
              </span>
              <!-- <span
                v-if="Boolean(hasMiniTriggers())"
                class="d-flex"
                style="width: 100%; justify-content: right"
              >
                <span class="circle-player"></span>
              </span>
              <div>
                <v-progress-circular
                  v-if="!canShowTimeOptions"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div> -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.miniTriggers"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- autoplay -->
            <v-btn
              id="smart-autoplay"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('autoplay')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  <span class="mr-1">{{
                    $t('message.pages.videoPage.settings.options.smartAutoplay')
                  }}</span>
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.autoplay"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- alternativeProgress -->
            <v-btn
              id="alternative-progress"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('alternativeProgress')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{
                    $t(
                      'message.pages.videoPage.settings.options.alternativeProgress'
                    )
                  }}
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.alternativeProgress"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- showPreviewThumbs -->
            <v-btn
              v-if="!video.hasOwnProperty('video_source')"
              id="show-preview-thumbs"
              class="settings-btn py-0 px-3"
              x-large
              text
              @click="handleSelectOption('showPreviewThumbs')"
            >
              <span class="d-flex">
                <p
                  v-if="isMiniMobileBreakpoint"
                  class="settings-btn--text --mobile"
                  v-html="
                    $t(
                      'message.pages.videoPage.settings.options.showPreviewThumbsMobile'
                    )
                  "
                ></p>
                <p class="settings-btn--text" v-else>
                  {{
                    $t(
                      'message.pages.videoPage.settings.options.showPreviewThumbs'
                    )
                  }}
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.showPreviewThumbs"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- saveProgress -->
            <v-btn
              id="save-progress"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('saveProgress')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{
                    $t('message.pages.videoPage.settings.options.saveProgress')
                  }}
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.saveProgress"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- offline video -->
            <v-btn
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('offlineVideo')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoOffline.title') }}
                </p>
                <base-chip class="new-item--chip" size="small"
                  ><span class="body-bold-smaller brand-20-darkest--text">{{
                    $i18next.t('message.newItemChip')
                  }}</span></base-chip
                >
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.offlinePlayer"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>
            <!-- speed -->
            <template>
              <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-btn
                      id="video-speed"
                      class="settings-btn py-0 px-3"
                      x-large
                      @click="handleSelectOption('speed')"
                      :disabled="video.hasOwnProperty('video_source')"
                      text
                      style="width: 100%"
                    >
                      <span class="d-flex">
                        <p
                          class="settings-btn--text"
                          style="align-self: center"
                        >
                          {{
                            $i18next.t('message.pages.videoSpeed.videoSpeed')
                          }}
                        </p>
                      </span>
                      <!-- <div>
                        <v-progress-circular
                          v-if="video.status != 'CONVERTED' && video.playable"
                          size="22"
                          width="2"
                          indeterminate
                          color="primary"
                        ></v-progress-circular>
                      </div> -->
                      <v-spacer></v-spacer>
                      <span
                        v-if="isSettingEnabled.turboSpeed"
                        class="d-flex enabled-setting-icon"
                      >
                        <v-icon size="16">panda-check-round</v-icon>
                      </span>
                      <v-icon class="settings-btn--icon" size="16"
                        >panda-go</v-icon
                      >
                    </v-btn>
                  </div>
                </template>
                <span>{{
                  $t('message.pages.externalSourceVideo.unavailableFor')
                }}</span>
              </v-tooltip>
              <v-btn
                v-else
                id="video-speed"
                class="settings-btn py-0 px-3"
                x-large
                @click="handleSelectOption('speed')"
                text
              >
                <span class="d-flex">
                  <p
                    v-if="isMiniMobileBreakpoint"
                    class="settings-btn--text --mobile"
                    v-html="
                      $t(
                        'message.pages.videoPage.settings.options.videoSpeedMobile'
                      )
                    "
                  ></p>
                  <p class="settings-btn--text" v-else>
                    {{
                      $i18next.t(
                        'message.pages.videoPage.settings.options.videoSpeed'
                      )
                    }}
                  </p>
                </span>
                <!-- <div>
                  <v-progress-circular
                    v-if="video.status != 'CONVERTED' && video.playable"
                    size="22"
                    width="2"
                    indeterminate
                    color="primary"
                  />
                </div> -->
                <v-spacer></v-spacer>
                <span
                  v-if="isSettingEnabled.turboSpeed"
                  class="d-flex enabled-setting-icon"
                >
                  <v-icon size="16">panda-check-round</v-icon>
                </span>
                <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
              </v-btn>
            </template>
            <!-- affiliate -->
            <v-btn
              id="affiliate"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('affiliate')"
              text
            >
              <span class="d-flex align-center">
                <p
                  v-if="isMobileBreakpoint"
                  class="settings-btn--text --mobile"
                  v-html="
                    $t(
                      'message.pages.videoPage.settings.options.affiliateMobile'
                    )
                  "
                ></p>
                <p class="settings-btn--text" v-else>
                  {{ $t('message.pages.videoPage.settings.options.affiliate') }}
                </p>
                <base-chip class="new-item--chip" size="small"
                  ><span class="body-bold-smaller brand-20-darkest--text">{{
                    $i18next.t('message.newItemChip')
                  }}</span></base-chip
                >
              </span>
              <v-spacer></v-spacer>
              <v-tag
                type="disabled"
                v-if="pandaBranding && isPandaBrandingBonusEnabled"
                size="xsmall"
                class="mr-2"
              >
                <span
                  class="body-regular-smaller neutral-40-light--text none-letter-space"
                  >{{ $t('message.affiliateVideoPage.bonus') }}</span
                >
              </v-tag>
              <span v-if="pandaBranding" class="d-flex enabled-setting-icon">
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- pip -->
            <v-btn
              id="pip"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('pip')"
              text
            >
              <span class="d-flex" v-if="isMobileBreakpoint">
                <p
                  class="settings-btn--text --mobile"
                  v-html="
                    $t('message.pages.videoPage.settings.options.pipMobile')
                  "
                ></p>
              </span>
              <span class="d-flex" v-else>
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoPage.settings.options.pip') }}
                </p>
              </span>
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.pip"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- Pixels -->
            <v-btn
              id="pixels"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('pixels')"
              text
              :disabled="!canShowTimeOptions"
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoPage.settings.options.pixels') }}
                </p>
              </span>
              <!-- <div>
                <v-progress-circular
                  v-if="!canShowTimeOptions"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div>  -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.pixels"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- buttons -->
            <v-btn
              id="custom-buttons"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('buttons')"
              :disabled="!canShowTimeOptions"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.video.buttons') }}
                </p>
              </span>
              <!-- <div
                v-if="video.config.ctas"
                class="d-flex"
                style="width: 100%; justify-content: right"
              >
                <span v-if="!haveButtons() || haveButtons().length !== 0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="d-inline-block">
                        <span class="circle-player"></span>
                      </div>
                    </template>
                    <span
                      >{{ haveButtons().length }}
                      {{
                        haveButtons().length > 1
                          ? $t('message.pages.video.buttons')
                          : $t('message.pages.video.button')
                      }}</span
                    >
                  </v-tooltip>
                </span>
              </div>
              <div>
                <v-progress-circular
                  v-if="!canShowTimeOptions"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div> -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.customButtons"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>

            <!-- showPreviewThumbs -->
            <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    id="show-preview-thumbs"
                    class="settings-btn py-0 px-3"
                    x-large
                    :disabled="video.hasOwnProperty('video_source')"
                    text
                    style="width: 100%"
                  >
                    <span class="d-flex">
                      <p class="settings-btn--text">
                        {{
                          $t(
                            'message.pages.videoPage.settings.options.showPreviewThumbs'
                          )
                        }}
                      </p>
                    </span>
                    <!-- <div>
                      <v-progress-circular
                        v-if="video.status != 'CONVERTED' && video.playable"
                        size="22"
                        width="2"
                        indeterminate
                        color="primary"
                      />
                    </div> -->
                    <v-spacer></v-spacer>
                    <span
                      v-if="isSettingEnabled.showPreviewThumbs"
                      class="d-flex enabled-setting-icon"
                    >
                      <v-icon size="16">panda-check-round</v-icon>
                    </span>
                    <v-icon class="settings-btn--icon" size="16"
                      >panda-go</v-icon
                    >
                  </v-btn>
                </div>
              </template>
              <!-- <span>{{
                $t('message.pages.externalSourceVideo.unavailableFor')
              }}</span> -->
            </v-tooltip>

            <!-- quality -->
            <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    id="quality"
                    class="settings-btn py-0 px-3"
                    x-large
                    @click="handleSelectOption('quality')"
                    :disabled="video.hasOwnProperty('video_source')"
                    text
                    style="width: 100%"
                  >
                    <span class="d-flex">
                      <p class="settings-btn--text">
                        {{
                          $t(
                            'message.pages.videoPage.settings.options.qualities'
                          )
                        }}
                      </p>
                    </span>
                    <div>
                      <v-progress-circular
                        v-if="video.status != 'CONVERTED' && video.playable"
                        size="22"
                        width="2"
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                      <v-icon class="settings-btn--icon" size="16"
                        >panda-go</v-icon
                      >
                    </div>
                  </v-btn>
                </div>
              </template>
              <span>{{
                $t('message.pages.externalSourceVideo.unavailableFor')
              }}</span>
            </v-tooltip>
            <v-btn
              v-else
              id="quality"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('quality')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoPage.settings.options.qualities') }}
                </p>
              </span>
              <div>
                <v-progress-circular
                  v-if="video.status != 'CONVERTED' && video.playable"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
                <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
              </div>
            </v-btn>

            <!-- drm :: security -->
            <v-tooltip bottom v-if="video.hasOwnProperty('video_source')">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    id="drm"
                    class="settings-btn py-0 px-3"
                    x-large
                    :disabled="video.hasOwnProperty('video_source')"
                    text
                    style="width: 100%"
                  >
                    <span class="d-flex">
                      <p class="settings-btn--text">
                        {{ $t('message.pages.videoPage.settings.options.drm') }}
                      </p>
                    </span>
                    <!-- <div>
                      <v-progress-circular
                        v-if="video.status != 'CONVERTED' && video.playable"
                        size="22"
                        width="2"
                        indeterminate
                        color="primary"
                      />
                    </div> -->
                    <v-spacer></v-spacer>
                    <span
                      v-if="isSettingEnabled.drm"
                      class="d-flex enabled-setting-icon"
                    >
                      <v-icon size="16">panda-check-round</v-icon>
                    </span>
                    <v-icon class="settings-btn--icon" size="16"
                      >panda-go</v-icon
                    >
                  </v-btn>
                </div>
              </template>
              <span>{{
                $t('message.pages.externalSourceVideo.unavailableFor')
              }}</span>
            </v-tooltip>
            <v-btn
              v-else
              id="drm"
              class="settings-btn py-0 px-3"
              x-large
              @click="handleSelectOption('drmSecurity')"
              text
            >
              <span class="d-flex">
                <p class="settings-btn--text">
                  {{ $t('message.pages.videoPage.settings.options.drm') }}
                </p>
              </span>
              <!-- <div>
                <v-progress-circular
                  v-if="video.status != 'CONVERTED' && video.playable"
                  size="22"
                  width="2"
                  indeterminate
                  color="primary"
                />
              </div> -->
              <v-spacer></v-spacer>
              <span
                v-if="isSettingEnabled.drm"
                class="d-flex enabled-setting-icon"
              >
                <v-icon size="16">panda-check-round</v-icon>
              </span>
              <v-icon class="settings-btn--icon" size="16">panda-go</v-icon>
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </span>
  </v-col>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import _ from 'lodash'
import colorHelpers from '@/utils/colorHelpers'
import i18n from '@/translations/lang'
import introMixinVideoPage from '@/utils/introVideoPage.mixin.js'
import FlagTooltip from '@/components/FlagTooltip.vue'
import FlagCards from '@/components/FlagCards.vue'
import langCodes from '@/utils/langCodes.mixin.js'
import settingsSearchBarMixin from '@/utils/settingsSearchBar.mixin.js'
import optionsAi from './videoPlayerOptionsAI/VideoPlayerOptionsAI.vue'
import Storage from '@/utils/Storage'
import SearchBar from '@/components/SearchBarVideoPage.vue'
import BaseChip from '@/components/BaseComponents/BaseChip.vue'
import logoBonificationMixin from '@/utils/logoBonification.mixin.js'
import playerApi from '@/services/playerApi'
import videosApi from '@/services/videosApi.js'
export default {
  name: 'VideoPlayerOptions',
  mixins: [
    introMixinVideoPage,
    langCodes,
    settingsSearchBarMixin,
    logoBonificationMixin,
  ],
  components: {
    FlagTooltip,
    FlagCards,
    optionsAi,
    SearchBar,
    BaseChip,
  },
  inject: ['introPageKey'],
  props: {
    handleSelectOption: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    profileId: null,
    readyForCustomize: false,
    isBeta: false,
    pandaBranding: false,
    isExternalTimeLoaded: false,
    introPath: 'message.pages.videoPage.introJS.',
    searchFuseOptions: {
      keys: [
        { name: 'setting_name', weight: 0.7 },
        { name: 'keywords', weight: 0.7 },
        { name: 'normalized_setting_name', weight: 0.7 },
        { name: 'related_settings', weight: 0.3 },
      ],
      includeScore: true,
      threshold: 0.3,
      includeMatches: true,
    },
    isPandaAiEnabledAndReady: false,
    showConversionTooltip: {
      sub: false,
      dub: false,
    },
  }),
  created() {
    if (this.$route.name !== 'NewLive') {
      if (!this.video.config?.bookMarks) this.video.config.bookMarks = []
      if (!this.video.config?.subtitles) this.video.config.subtitles = []
      if (!this.video?.audios) this.video.audios = []
      this.UPDATE_VIDEO(this.video)
    }
  },
  async mounted() {
    this.$root.$on('updatedExternalTimeVideo', () => {
      this.isExternalTimeLoaded = true
    })
    this.$root.$on('fetchSubtitles', async () => {
      await this.fetchSubtitles({
        libraryId: this.video.pullzone_name,
        videoId: this.video.video_external_id,
      })
    })
    this.isBeta = location.search.match('beta')
    await this.handleSettingsLoading()
    if (this.video.config) this.checkProfile()
    const ret = await playerApi.getPlayer()
    this.pandaBranding = ret.data?.config?.pandaBranding
    this.haveButtons()
  },
  computed: {
    ...mapGetters('video', ['activeAudios']),
    ...mapGetters('user', ['canSeePlanInfo']),
    ...mapState({
      video: (state) => state.video.video,
      profile: (state) => state.video.profile,
      profile_id: (state) => state.video.profile_id,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
      isTabletBreakpoint: (state) => state.user.isTabletBreakpoint,
      user: (state) => state.user,
      isPandaBrandingBonusEnabled: (state) =>
        state.user.isPandaBrandingBonusEnabled,
    }),
    ...mapState('video', {
      _player: (state) => state.player,
    }),
    isConverting() {
      const status = this.video.status
      return ['CONVERTING', 'DRAFT'].includes(status)
    },
    isMiniMobileBreakpoint() {
      return this.isMobileBreakpoint / 2 < 384
    },
    isPlayable() {
      return this.video.playable
    },
    checkForSubtitles() {
      return this.video.config?.subtitles?.length > 0
    },
    subtitlesTooltipInfo() {
      if (
        this.video?.config?.subtitles &&
        this.video?.config?.subtitles?.length
      ) {
        return {
          subtitles: {
            label: this.$i18next.t('message.ai.subtitles.subtitle'),
            iconFile: 'cc_tooltip',
            items: this.video.config.subtitles.map((item) =>
              this.getPreparedSrcLang(item.srclang)
            ),
          },
        }
      }
      return false
    },
    audiosTooltipInfo() {
      if (this.activeAudios && this.activeAudios?.length) {
        return {
          audios: {
            label: this.$i18next.t('message.ai.dubbings.dubbing'),
            iconFile: 'dubbing',
            items: this.activeAudios.map((item) =>
              this.getPreparedSrcLang(item.lang)
            ),
          },
        }
      }
      return false
    },
    canShowTimeOptions() {
      if (
        this.video.hasOwnProperty('video_source') &&
        !this.isExternalTimeLoaded &&
        !this.video.length
      ) {
        return false
      }
      return true
    },
    hasEditDrmPerm() {
      return (
        (this.$store.state.user?.userPermissions?.includes('put-video') &&
          this.$store.state.user?.userPermissions?.includes('put-watermark')) ??
        false
      )
    },
    hasPixels() {
      // console.log(
      //   '👀 ~ hasPixels ~ this._player?.config:',
      //   Object.keys(this._player).length
      // )
      // if (!Object.keys(this._player).length) return false
      // debugger
      // if (
      //   !this.checkChangesInState(
      //     this.profile.vsl?.fbPixel,
      //     this._player.config?.fbPixel
      //   ) ||
      //   !this.checkChangesInState(
      //     this.profile.vsl?.gtagIds,
      //     this._player.config?.gtagIds
      //   ) ||
      //   (this.video.config?.customPixels &&
      //     this.video.config?.customPixels.length)
      // )
      //   return true
      return false
    },
    checkBigPlayButton() {
      if (this.profile.controls.indexOf('play-large') === -1) {
        return true
      } else if (
        (this.profile?.general.bigPlayButtonSize !== 150 &&
          this.profile?.general.bigPlayButtonSize !== 100) ||
        (this.profile?.general.bigPlayButtonIconSize !== 86 &&
          this.profile?.general.bigPlayButtonIconSize !== 57)
      ) {
        return true
      } else {
        return false
      }
    },
    isSettingEnabled() {
      try {
        return {
          bigPlayButton: this.checkIfSettingIsEnabled('bigPlayButton'),
          thumbnails: this.checkIfSettingIsEnabled('thumbnails'),
          bookMarks: this.checkIfSettingIsEnabled('bookMarks'),
          miniTriggers: this.checkIfSettingIsEnabled('miniTriggers'),
          autoplay: this.checkIfSettingIsEnabled('autoplay'),
          alternativeProgress: this.checkIfSettingIsEnabled(
            'alternativeProgress'
          ),
          showPreviewThumbs: this.checkIfSettingIsEnabled('showPreviewThumbs'),
          saveProgress: this.checkIfSettingIsEnabled('saveProgress'),
          turboSpeed: this.checkIfSettingIsEnabled('turboSpeed'),
          pandaBranding: this.checkIfSettingIsEnabled('pandaBranding'),
          pip: this.checkIfSettingIsEnabled('pip'),
          pixels: this.checkIfSettingIsEnabled('pixels'),
          customButtons: this.checkIfSettingIsEnabled('customButtons'),
          drm: this.checkIfSettingIsEnabled('drm'),
          offlinePlayer: this.checkIfSettingIsEnabled('offlinePlayer'),
        }
      } catch (e) {
        return !e
      }
    },
  },
  async beforeMount() {
    const {
      data: { config },
    } = await videosApi.getAVideo(this.video.id)
    this.UPDATE_VIDEO_CONFIG(config)
  },
  watch: {
    'video.config': function () {
      this.checkProfile()
    },
    intro_status(value) {
      const key = 'video-page-options'

      const hasFinished = value === 'finished'
      const isVideoPage = this.intro_key === this.introPageKey
      const isVideoOptions = this.intro_key === key

      if (hasFinished && isVideoPage) {
        this.setIntroJS({
          key,
          delay: 350,
          type: 'hint',
          reset: true,
        })
        setTimeout(() => {
          this.intro_key = this.introPageKey
          this.intro_type = 'intro'
        }, 400)
      }

      if (hasFinished && isVideoOptions) {
        this.intro_key = this.introPageKey
        this.intro_type = 'intro'
      }
    },
  },
  beforeDestroy() {
    this.intro_key = this.introPageKey
    this.intro_type = 'intro'
    this.intro_status = 'finished'
    this.$root.$off('fetchSubtitles')

    this.hideIntroJS()
  },
  methods: {
    ...mapActions('video', [
      'getVideoPlayer',
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
      'fetchSubtitles',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_VIDEO',
      'UPDATE_VIDEO_PROFILE',
    ]),
    ...mapMutations('video', ['UPDATE_VIDEO_CONFIG']),
    handleConversionTooltip(e, target) {
      const isHovering = ['mouseenter'].includes(e.type)

      if (isHovering && this.isConverting) {
        this.showConversionTooltip[target] = true
      } else {
        this.showConversionTooltip[target] = false
      }
    },
    getItemsCountryCode(items) {
      const finalItems = items.map((item) => this.getCountryCodeBySrcLang(item))
      return finalItems
    },
    async checkProfile() {
      const addOpacity = (color, opacity = 'B3') =>
        `${color}${opacity}`.slice(0, 9)

      this.readyForCustomize = true
      this.profileId = this.video.config?.profile

      if (!this.profileId) {
        this.video.config['profile'] = this.profileId
      }

      const profile = {
        colors: {
          captionsColor:
            this.profile.colors?.captionsColor ??
            this._player.config?.captionsColor,
          captionsBackgroundColor:
            this.profile.colors?.captionsBackgroundColor ??
            this._player.config?.captionsBackgroundColor,
          controlsColor: this.profile.colors?.controlsColor
            ? this.profile.colors?.controlsColor
            : this._player.config?.controlsColor,
          primaryColor: this.profile.colors?.primaryColor
            ? this.profile.colors?.primaryColor
            : this._player.config?.primaryColor,
        },
        general: {
          bigPlayButtonSize: this.profile.general?.bigPlayButtonSize
            ? this.profile.general?.bigPlayButtonSize
            : 100,
          bigPlayButtonIconSize: this.profile.general?.bigPlayButtonIconSize
            ? this.profile.general?.bigPlayButtonIconSize
            : 57,
          showPreviewThumbs: this.profile.general?.showPreviewThumbs ?? true,
          captionsFontSize: this.profile.general?.captionsFontSize
            ? this.profile.general?.captionsFontSize
            : this._player.config?.captionsFontSize,
          startsWithSubtitle: this.profile.general?.startsWithSubtitle
            ? this.profile.general?.startsWithSubtitle
            : false,
          default_speed: this.profile.general?.default_speed
            ? this.profile.general?.default_speed
            : 1,
        },
        controls: this.profile?.controls
          ? this.profile?.controls
          : this._player.config.controls,
        hideControlsOnStart: this.profile?.hideControlsOnStart
          ? this.profile?.hideControlsOnStart
          : false,
        saveProgress: {
          initQuality: this.profile.saveProgress?.initQuality
            ? this.profile.saveProgress?.initQuality
            : undefined,
          saveProgress:
            this.profile.saveProgress?.saveProgress !==
              this._player.config?.saveProgress &&
            this.profile.saveProgress?.saveProgress !== undefined
              ? this.profile.saveProgress?.saveProgress
              : this._player.config?.saveProgress,
          saveProgressScreen: this.profile.saveProgress?.saveProgressScreen
            ? this.profile.saveProgress?.saveProgressScreen
            : false,
          saveProgressTitle: this.profile.saveProgress?.saveProgressTitle
            ? this.profile.saveProgress?.saveProgressTitle
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.titlePlaceholder'
              ),
          saveProgressBackgroundOpacity: this.profile.saveProgress
            ?.saveProgressBackgroundOpacity
            ? this.profile.saveProgress?.saveProgressBackgroundOpacity
            : 0.8,
          saveProgressBackgroundOpacityFormated:
            this.profile.saveProgress?.saveProgressBackgroundOpacity * 100
              ? this.profile.saveProgress?.saveProgressBackgroundOpacity * 100
              : 80,
          saveProgressButton1Title: this.profile.saveProgress
            ?.saveProgressButton1Title
            ? this.profile.saveProgress?.saveProgressButton1Title
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.playButtonPlaceholder'
              ),
          saveProgressButton2Title: this.profile.saveProgress
            ?.saveProgressButton2Title
            ? this.profile.saveProgress?.saveProgressButton2Title
            : i18n.t(
                'message.pages.video.titleInputsResumePlay.restartButtonPlaceholder'
              ),
        },
        updated_at: this.profile.updated_at
          ? this.profile.updated_at
          : undefined,
        vsl: {
          alternativeProgress: this.profile.vsl?.alternativeProgress
            ? this.profile.vsl?.alternativeProgress
            : false,
          alternativeProgress2xLimit: this.profile.vsl
            ?.alternativeProgress2xLimit
            ? this.profile.vsl?.alternativeProgress2xLimit
            : 50,
          alternativeProgressHeight: this.profile.vsl?.alternativeProgressHeight
            ? this.profile.vsl.alternativeProgressHeight
            : 10,
          alternativeProgressVelocity: this.profile.vsl
            ?.alternativeProgressVelocity
            ? this.profile.vsl?.alternativeProgressVelocity
            : 2,
          alternativeProgressDefaultVelocity: this.profile.vsl
            ?.alternativeProgressDefaultVelocity
            ? this.profile.vsl?.alternativeProgressDefaultVelocity
            : false,
          autoplay: this.profile.vsl?.autoplay
            ? this.profile.vsl?.autoplay
            : false,
          mutedIndicatorIcon: this.profile.vsl?.mutedIndicatorIcon
            ? this.profile.vsl?.mutedIndicatorIcon
            : false,
          mutedIndicatorFontSize: this.profile.vsl?.mutedIndicatorFontSize
            ? this.profile.vsl?.mutedIndicatorFontSize
            : 22,
          mutedIndicatorTextTop: this.profile.vsl?.mutedIndicatorTextTop
            ? this.profile.vsl?.mutedIndicatorTextTop
            : i18n.t('message.pages.video.mutedIndicator.topTextDefault'),
          mutedIndicatorTextBottom: this.profile.vsl?.mutedIndicatorTextBottom
            ? this.profile.vsl?.mutedIndicatorTextBottom
            : i18n.t('message.pages.video.mutedIndicator.bottomTextDefault'),
          mutedIndicatorBackgroundColor: this.profile.vsl
            ?.mutedIndicatorBackgroundColor
            ? this.profile.vsl?.mutedIndicatorBackgroundColor
            : colorHelpers.hexToRgba(
                addOpacity(this._player.config?.primaryColor)
              ),
          mutedIndicatorTextColor: this.profile.vsl?.mutedIndicatorTextColor
            ? this.profile.vsl?.mutedIndicatorTextColor
            : colorHelpers.hexToRgba(
                addOpacity(this._player.config?.controlsColor, 'ff')
              ),
          restartAfterEnd: this.profile.vsl?.restartAfterEnd
            ? this.profile.vsl?.restartAfterEnd
            : false,
          disablePause: this.profile.vsl?.disablePause
            ? this.profile.vsl?.disablePause
            : false,
          disableForward: this.profile.vsl?.disableForward
            ? this.profile.vsl?.disableForward
            : false,
          refCode:
            this.profile.vsl?.refCode !== this._player.config?.refCode &&
            this.profile.vsl?.refCode !== undefined
              ? this.profile?.vsl?.refCode
              : this._player.config?.refCode,
          fbPixel:
            this.profile.vsl?.fbPixel !== this._player.config?.fbPixel &&
            this.profile.vsl?.fbPixel !== undefined
              ? this.profile?.vsl.fbPixel
              : this._player.config?.fbPixel,
          gtagIds:
            this.profile.vsl?.gtagIds !== this._player.config?.gtagIds &&
            this.profile.vsl?.gtagIds !== undefined
              ? this.profile?.vsl.gtagIds
              : this._player.config?.gtagIds,
          pandaBranding:
            this.profile.vsl?.pandaBranding !==
              this._player.config?.pandaBranding &&
            this.profile.vsl?.pandaBranding !== undefined
              ? this.profile.vsl?.pandaBranding
              : this._player.config?.pandaBranding,
          mutedIndicatorAnimation: this.profile.vsl?.mutedIndicatorAnimation
            ? this.profile.vsl?.mutedIndicatorAnimation
            : this.profile.vsl?.mutedIndicatorIcon
            ? 'default'
            : 'impact',
          customMutedIndicatorId:
            this.profile.vsl?.customMutedIndicatorId || null,
          mutedIndicatorLoop: this.profile.vsl?.mutedIndicatorLoop || false,
          mutedIndicatorLoopDuration:
            this.profile.vsl?.mutedIndicatorLoopDuration || 15,
          smartAutoplay: this.profile.vsl?.smartAutoplay || false,
          mutedIndicatorSize: this.profile.vsl?.mutedIndicatorSize || 'small',
          mutedIndicatorBorderSize:
            this.profile.vsl?.mutedIndicatorBorderSize || 2,
          defaultLanguage: this.profile.vsl?.defaultLanguage,
          smallMutedIcon: this.profile.vsl?.smallMutedIcon ?? true,
        },
      }

      if (
        this.video.hasOwnProperty('video_source') &&
        !this.video.config.profile
      ) {
        profile.vsl.smartAutoplay =
          profile.vsl.autoplay =
          profile.vsl.mutedIndicatorIcon =
          profile.vsl.alternativeProgress =
            true
        profile.vsl.mutedIndicatorAnimation = 'impact'

        this.UPDATE_VIDEO_PROFILE(profile)
        await this.createProfile(this.video.id)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profile_id, this.video.id])
        await this.updateProfile([this.profile_id, profile])
      } else {
        this.UPDATE_VIDEO_PROFILE(profile)
      }
    },
    checkChangesInState(currentState, defaultState) {
      return _.isEqual(currentState, defaultState)
    },
    isDefaultThumbnail() {
      return this.video?.thumbnail?.includes('thumbnail.jpg') || false
    },
    havePauseThumbnail() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      if (!arrayCtas) {
        return (arrayCtas = [])
      }
      return arrayCtas.filter((item) => !item.title)
    },
    haveButtons() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      let outsideCta = this.$store.state.video.video.config?.outsideCta
      if (!arrayCtas) {
        arrayCtas = []
      }
      if (!outsideCta) {
        outsideCta = []
      }
      let arrayCtasFiltered = arrayCtas.filter((item) => item.title)
      return arrayCtasFiltered.concat(outsideCta)
    },
    hasMiniTriggers() {
      let arrayCtas = this.$store.state.video.video.config?.ctas
      if (!arrayCtas) {
        arrayCtas = []
      }
      let arrayCtasFiltered = arrayCtas.filter(
        (item) => item.metadata?.isMiniTrigger
      )
      return arrayCtasFiltered?.length
    },
    checkIfSettingIsEnabled(param) {
      if (!Object.keys(this.profile).length) return
      if (!Object.keys(this._player).length) return
      if (param === 'bigPlayButton') {
        if (!this.profile.controls) return
        const isEnabled =
          this.profile.controls.indexOf('play-large') === -1 ? false : true
        return isEnabled
      }
      if (param === 'thumbnails') {
        let arrayCtas = this.$store.state.video.video.config?.ctas
        if (!arrayCtas) return false
        const thumbnails = arrayCtas.filter(
          (item) => item.type === 'end' || item.type === 'pause'
        )
        const hasEditedThumbs = thumbnails && thumbnails.length ? true : false
        const isEnabled =
          !this.isDefaultThumbnail() || hasEditedThumbs ? true : false
        return isEnabled
      }
      if (param === 'bookMarks') {
        let bookMarks = this.video.config.bookMarks
        const isEnabled = bookMarks && bookMarks.length ? true : false
        return isEnabled
      }
      if (param === 'miniTriggers') {
        let arrayCtas = this.$store.state.video.video.config?.ctas
        if (!arrayCtas) return false
        let hasMiniTrigger = arrayCtas.filter(
          (item) => item.metadata?.isMiniTrigger
        )
        const isEnabled = hasMiniTrigger && hasMiniTrigger.length ? true : false
        return isEnabled
      }
      if (param === 'autoplay') {
        if (!this.profile.vsl) return
        const isEnabled =
          this.profile.vsl.autoplay || this.profile.vsl.mutedIndicatorIcon
            ? true
            : false
        return isEnabled
      }
      if (param === 'alternativeProgress') {
        const isEnabled = this.profile.vsl.alternativeProgress ? true : false
        return isEnabled
      }
      if (param === 'showPreviewThumbs') {
        const isEnabled = this.profile.general.showPreviewThumbs ? true : false
        return isEnabled
      }
      if (param === 'saveProgress') {
        const isEnabled = this.profile.saveProgress?.saveProgress ? true : false
        return isEnabled
      }
      if (param === 'turboSpeed') {
        const isEnabled =
          this.profile.general.default_speed !== 1 ? true : false
        return isEnabled
      }
      if (param === 'pandaBranding') {
        const isEnabled = this.profile.vsl.pandaBranding ? true : false
        return isEnabled
      }
      if (param === 'pip') {
        const isEnabled = Storage.getItem('pipScrollFollow') ?? false
        return isEnabled
      }
      if (param === 'pixels') {
        const isEnabled = this.hasPixels
        return isEnabled
      }
      if (param === 'customButtons') {
        let customButtonsCta = []
        let arrayCtas = this.$store.state.video.video.config?.ctas

        let outsideCta = this.$store.state.video.video.config?.outsideCta
        if (arrayCtas) {
          customButtonsCta = arrayCtas.filter(
            (cta) =>
              cta.type !== 'end' &&
              cta.type !== 'pause' &&
              !cta?.metadata?.isMiniTrigger
          )
        }
        const isEnabled =
          outsideCta?.length || customButtonsCta.length ? true : false
        return isEnabled
      }
      if (param === 'drm') {
        const isEnabled = this.isDrmActiveForThisVideo()
        return isEnabled
      }
      if (param == 'offlinePlayer') {
        const { config } = this.$store.state.video.video
        const offlineDataSession = sessionStorage.getItem(
          `video_offline_enabled_${this.video.id}`
        )
        const isOfflineEnabled = offlineDataSession === 'true'
        const isEnabled =
          offlineDataSession !== null
            ? isOfflineEnabled
            : config?.video_offline_enabled
        return isEnabled
      }
    },
    isDrmActiveForThisVideo() {
      const drmData = this.$store.state.user.drmData
      if (!drmData.isDrmActive) return false
      const isActive = drmData.drmGroups.some(
        (obj) => obj.active && obj.video_ids.includes(this.video.id)
      )
      return isActive
    },

    handleOpenAnalytics() {
      const routeData = this.$router.resolve({
        name: 'AnalyticsV2Account',
        query: {
          v: this.video.video_external_id,
        },
      })
      window.open(routeData.href, '_blank')
    },

    async handleSettingsLoading() {
      const playerConfig = this.$store.state.video.player?.config
      if (!playerConfig) await this.getVideoPlayer()
      return
    },
  },
}
</script>

<style lang="scss">
.converting-video-tooltip {
  background-color: varFigma(neutral-0-darkest);
  border-color: varFigma(neutral-0-darkest);
  border-radius: $spacing-02;
  padding: $spacing-02 $spacing-03;
  span {
    color: varFigma(neutral-95-lightest);
    white-space: nowrap;
    cursor: help;
  }
  &--offset {
    margin-top: -20px;
  }
  &--btn {
    span {
      cursor: pointer;
      color: varFigma(neutral-30-base) !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.new-item--chip {
  background: varFigma(brand-90) !important;
  border: none !important;
  margin-left: $spacing-03 !important;
  border-radius: $spacing-05 !important;
  padding: 0 $spacing-03 !important;
}
.none-letter-space {
  letter-spacing: 0;
}
.video-page-settings {
  .settings-btn {
    justify-content: space-between;
    text-transform: unset;
    border-bottom: 1px solid varFigma(neutral-95-lightest);
    &.v-btn {
      border-radius: 0px;
      width: 100%;
    }
    &--text {
      color: varFigma(neutral-30-base);
      font-family: Rubik;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%;
      margin: 0 !important;

      &.--mobile {
        text-align: start;
      }
    }
    &--icon {
      color: varFigma(neutral-20-dark);
    }
    &.v-btn::before {
      background-color: inherit !important;
    }
    &:hover {
      background-color: varFigma(neutral-99) !important;
      border-radius: 4px !important;
    }
    .color-disabled {
      color: varFigma(neutral-70) !important;
    }
  }
  .cursor-disabled {
    cursor: not-allowed;
  }
  .enabled-setting-icon {
    border-radius: $spacing-03;
    margin-right: $spacing-03;
    background-color: varFigma(feedback-success-light);
    padding: 0 $spacing-02;
  }
}

.optionsText {
  font-weight: 400;
  margin: 0 0 0 5px !important;
  letter-spacing: normal;
  padding: 2px 0;
}

.container-list-icon {
  max-width: 24px;
  max-height: 24px;
}

.newSectionStyle {
  background-color: #ebedff;
  color: var(--v-primary-base);
  font-weight: 500;
  text-transform: uppercase;
  width: 68px;
  display: flex;
  border-radius: 24px;
}
.newSectionStyle span {
  margin: 0 auto;
}

.options-search-filter::v-deep {
  .v-input__control .v-input__slot {
    .v-input__prepend-inner {
      margin: 0 !important;
      align-self: center !important;
      .v-icon {
        color: varFigma(neutral-60) !important;
      }
    }
  }
}

.body-label-caption {
  color: varFigma(neutral-60) !important;
  font-family: Rubik;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.66px;
  text-transform: uppercase;
}
</style>
